import { calcRelocation, hsvToHexColorCode } from '@/util/color-util';
import { GadgetMakerBase } from './GadgetMakerBase';
import { IDomain, IGraphData } from '@/class/models/typed/gadget/basicGadget';
import { MaterialId } from '@/class/common/const/gadget-material-id';
import { calcOpCountHeatmapWidth } from '@/class/common/util/op-count-heatmap-util';
import localize from '@/class/common/localize/localize-util';
import { HEATMAP_OP_COUNT_DEFAULT_MAX } from '@/class/common/const/operation-count-anyalysis-const';

const HUE_ON_OP_COUNT_MIN = 240;   // ヒートマップの色相。操作回数下限時。
const HUE_ON_OP_COUNT_MAX = 0;     // ヒートマップの色相。操作回数上限時。
export const OP_COUNT_MIN = 0;    // ヒートマップ表示上の操作回数下限

/**
 * ガジェットの作成者。ヒートマップ用。
 */
export class GadgetMakerOpCountHeatmap extends GadgetMakerBase {
  private readonly HEATMAP_HEIGHT = 24;  // ヒートマップのY軸セル数
  private readonly NO_DATA_COLOR = '#7b7b7bff';  // データが無い箇所の色

  private heatmapWidth: number;  // ヒートマップのX軸セル数
  private dimentionMaterialId: number; // ヒートマップのX軸ラベルの種類（月、日、曜日）

  constructor(gadgetDatas: IGraphData[], materials: IDomain[]) {
    super(gadgetDatas, materials);

    // DIMENTIONマテリアルID検索。
    this.dimentionMaterialId = findDimention( materials );

    if ( this.gadgetDatas && this.gadgetDatas.length > 0) {
      // 0番目のデータのlabelを選択日付とみなす。
      const currentDate = new Date( this.gadgetDatas[0].label );
      // ヒートマップ横幅セル数算出
      this.heatmapWidth = calcOpCountHeatmapWidth(this.dimentionMaterialId, currentDate);
    }
  }

  /**
   * [abstract] Chart.jsオプションの生成。
   * @param srcOptionObj 元になるオプション。新規設定を加えて新規オプションを返却する。
   */
  public generateOptionObj(srcOptionObj: Readonly<any>): any {
    const newOptionObj = { ...srcOptionObj }; // copy

    newOptionObj.scales.xAxes[0].display = true;
    newOptionObj.scales.yAxes[0].display = true;

    newOptionObj.scales.yAxes[0].ticks.min = 0;
    newOptionObj.scales.yAxes[0].ticks.max = 24;
    newOptionObj.scales.yAxes[0].ticks.stepSize = 0;
    newOptionObj.scales.yAxes[0].ticks.display = true;

    // Y軸目盛りラベルコールバック
    newOptionObj.scales.yAxes[0].ticks.callback = (value: any, index: any, values: any[]) => {
      // 0〜24 を 00:00〜24:00形式にして表示。
      return convertYIndexToScaleLabel(value);
    };

    // 積み上げ棒グラフにする
    newOptionObj.scales.xAxes[0].stacked = true;
    newOptionObj.scales.yAxes[0].stacked = true;

    // ツールチップ
    newOptionObj.tooltips.display = true;
    newOptionObj.tooltips.callbacks = {
        title: (tooltipItems, data) => {
          // note: titleではtooltip_itemsは配列。
          // console.log(`tooltipItems=${JSON.stringify(tooltipItems)}`)
          const ix = tooltipItems[0].index;
          if ( 0 <= ix && ix < this.gadgetDatas.length ) {
            const iy = tooltipItems[0].datasetIndex;
            const gadgetData = this.gadgetDatas[ix];
            if ( 0 <= iy && iy < gadgetData.heatmapLineCells.length ) {
              const xLabel = convertRawToDispalyLabel(this.dimentionMaterialId, gadgetData.label, true);
              return `${xLabel} ${convertYIndexToScaleLabel(iy)}`; // X軸ラベルと時間表示
            }
          }
          return '-'; // データがない場合。
        },
        label: (tooltipItem, data) => {
          const ix = tooltipItem.index;
          if ( ix < this.gadgetDatas.length ) {
            const gadgetData = this.gadgetDatas[ix];
            const iy = tooltipItem.datasetIndex;
            if ( iy < gadgetData.heatmapLineCells.length ) {
              const cellData = gadgetData.heatmapLineCells[iy];
              return '平均操作回数/1h：' + Math.floor(cellData.value) + '回';
            }
          }
          return '-'; // データがない場合。
        },
    };

    return newOptionObj;
  }

  /**
   * Chart.jsデータセットの作成。
   */
  public generateDatasets(): any[] {
    const datasets = [];

    // データ行数取得
    const numGadgetData = this.gadgetDatas.length;
    // エラーチェック
    if ( numGadgetData <= 0 ) {
      console.warn('GadgetMakerOpCountHeatmap.generateDatasets() gadgetDatasのデータがありません。');
      return datasets;
    }

    // ヒートマップデータセット作成
    // note takezawa gadgetDatasは列データリスト、ヒートマップのdatasetsは行データリストなので変換する。
    for (let i = 0; i < this.HEATMAP_HEIGHT; i++) {
      const newDataset = {
        label: `${i}:00 - ${i + 1}:00`,
        data: new Array(this.heatmapWidth).fill(1),
        borderWidth: 0,
        borderHeight: 0,
        borderColor: '#FFFFFF',
        barPercentage: 1,
        categoryPercentage: 1,
        backgroundColor: [] as string[],
      };

      const colorbarValueMax = this.getColorbarValueMax();
      const bgColors: string[] = new Array();
      for (let k = 0; k < this.heatmapWidth; ++k) {
        // gadgetDatasデータがない場合
        if ( k < this.gadgetDatas.length
          && i < this.gadgetDatas[k].heatmapLineCells.length
        ) {
          // データがある場合
          // 操作回数を色に変換。
          const gadgetData = this.gadgetDatas[k];
          const opCount = gadgetData.heatmapLineCells[i].value;
          const color = clacValueToColor(opCount, OP_COUNT_MIN, colorbarValueMax );
          bgColors.push( color );
        } else {
          // データがない場合
          bgColors.push( this.NO_DATA_COLOR );
        }
      }
      newDataset.backgroundColor = [...bgColors];
      datasets.push(newDataset);
    }

    return datasets;
  }

  /**
   * ラベルリストの作成。
   */
  public generateLabels(): any {
    const labels = [];
    for (const gadgetData of this.gadgetDatas) {
      const label = convertRawToDispalyLabel(this.dimentionMaterialId, gadgetData.label);
      labels.push( label );
    }
    return labels;
  }

  /**
   * ヒートマップカラーバーの最大値算出。
   * @returns
   */
  public getColorbarValueMax(): number {
    if ( this.gadgetDatas && this.gadgetDatas.length > 0) {
      return this.gadgetDatas[0].heatmapColorbarValueMax;
    }
    return HEATMAP_OP_COUNT_DEFAULT_MAX;
  }

} // end class

// ----------------------------------------------------------------------------
// ローカル関数。
// ----------------------------------------------------------------------------
/**
 * DIMENTIONのマテリアルID検索。
 * @param materials
 * @returns
 */
function findDimention(materials: IDomain[]): number {
  if ( !materials ) {
    console.error('materials is empty.');
    return undefined;
  }

  for (const material of materials) {
    switch ( material.id ) {
    case MaterialId.DIMENSION_MONTH:
    case MaterialId.DIMENSION_DATE:
    case MaterialId.DIMENSION_DAY_OF_WEEK:
      return material.id;
    }
  }
  console.error('ヒートマップに対応するDIMENTIONが見つかりません。');
  return undefined;
}

/**
 * バックエンドから送信されたGadgetDatas[].labelをグラフ表示用のラベルに変換。
 * @param dimentionMaterialId
 * @param gadgetDataLabel GadgetDatas[].label（日付または曜日番号）
 * @param isAddDayOfWeek 曜日を追加するか。
 */
function convertRawToDispalyLabel(dimentionMaterialId: number, gadgetDataLabel: string, isAddDayOfWeek: boolean= false): string {
  let label: string;
  switch ( dimentionMaterialId ) {
  case MaterialId.DIMENSION_MONTH:
    {
      // gadgetDatas[].labelは日付が設定されているので、月を抽出して月文字列に変換。
      const date = new Date(gadgetDataLabel);
      label = localize.getShortMonthString(date.getMonth());
      // 曜日の追加
      if ( isAddDayOfWeek ) {
        label += localize.getShortDayOfWeekStringParen(date.getDay());
      }
    }
    break;
  case MaterialId.DIMENSION_DATE:
    {
      // gadgetDatas[].labelは日付が設定されているので、日を抽出して日文字列に変換。
      const date = new Date(gadgetDataLabel);
      label = localize.getDayString(date.getDate());
      // 曜日の追加
      if ( isAddDayOfWeek ) {
        label += localize.getShortDayOfWeekStringParen(date.getDay());
      }
    }
    break;
  case MaterialId.DIMENSION_DAY_OF_WEEK:
    // gadgetDatas[].labelは曜日番号が設定されているので、曜日文字列に変換。
    {
      const dayOfWeek = Number(gadgetDataLabel);
      label = localize.getShortDayOfWeekString(dayOfWeek);
    }
    break;
  default:
    console.error(`ヒートマップに対応するdimentionMaterialId(${dimentionMaterialId})ではありません。`);
    label = '-';
    break;
  }
  return label;
}

/**
 * Y軸セルインデックスををY軸目盛りラベルに変換。
 * 例:
 *  0     => 00:00
 *  12    => 12:00
 *  12.5  => 12:30
 * @param yIndex Y軸インデックス。小数でも構わない。
 * @returns
 */
function convertYIndexToScaleLabel(yIndex: number): string {
  const hourInt = Math.trunc(yIndex);
  const hourStr = hourInt.toString().padStart(2, '0');
  const minuteDecimal = yIndex - hourInt;
  let minuteStr = (60 * minuteDecimal).toString().slice(0, 2);
  minuteStr = minuteStr.padStart(2, '0');
  return hourStr + ':' + minuteStr;
}

// ----------------------------------------------------------------------------
// グローバル関数。
// ----------------------------------------------------------------------------
/**
 * 操作回数から色に変換。
 * @param value
 * @param min
 * @param max
 * @returns
 */
export function clacValueToColor(value: number, min: number, max: number): string {
  const hue = calcRelocation( value, min, max, HUE_ON_OP_COUNT_MIN, HUE_ON_OP_COUNT_MAX);
  return hsvToHexColorCode(hue, 1, 1);
}
